import React from "react";
import ProductEditDialog from "./ProductEditDialog";

import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";

import { productColumns } from "@/lib/columns";
import { products } from "@/lib/constants";
import { Link } from "react-router";
import {
  Loader2,
  MoreHorizontal,
  PlusCircle,
  Trash,
  Trash2,
} from "lucide-react";
import { ProductType } from "@/lib/interfaces";
import { axiosPrivate } from "@/api/axios";
import { toast } from "sonner";
import { Row } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DropdownMenu } from "@/components/ui/dropdown-menu";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { Pencil } from "lucide-react";

function Products() {
  const queryClient = useQueryClient();
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingProduct, setEditingProduct] = React.useState<ProductType | null>(null);
  const confirmActionButtonRef = React.useRef<() => void>(() => {});

  const { data: rawData, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const res = await axiosPrivate.get("/products/");
      if (res.data.success) {
        return res.data.data;
      } else {
        toast.error("Failed to fetch products. Please try again later.");
        throw new Error("Failed to fetch products.");
      }
    },
    placeholderData: [],
  });

  // Sort products by ID ascending
  const data = React.useMemo(() => {
    if (!rawData) return [];
    return [...rawData].sort((a, b) => Number(a.id) - Number(b.id));
  }, [rawData]);
  // Fetch product categories for edit dialog
  const { data: categories } = useQuery({
    queryKey: ["productCategories"],
    queryFn: async () => {
      const res = await axiosPrivate.get("/categories/");
      return res.data.data || [];
    },
    placeholderData: [],
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await axiosPrivate.delete(`/products/${id}/`);
      if (!res.data.success) {
        throw new Error("Failed to delete product.");
      }
      return id;
    },
    onMutate: async (id: number) => {
      toast.loading("Deleting product...");
    },
    onSuccess: (id: number) => {
      queryClient.setQueryData<ProductType[]>(["products"], (oldData) =>
        oldData ? oldData.filter((product) => product.id !== id) : []
      );
      toast.dismiss();
      toast.info("Product deleted successfully.");
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to delete product. Please try again later.");
    },
  });

  function handleDelete(id: number) {
    deleteProductMutation.mutate(id);
  }

  const actions = {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<ProductType> }) => {
      return (
        <>
          <ActionsMenu
            onEdit={() => {
              setEditingProduct(row.original);
              setEditDialogOpen(true);
            }}
            onDelete={() => {
              confirmActionButtonRef.current = () => handleDelete(row.original.id);
              setOpenConfirmDialog(true);
            }}
          />
        </>
      );
    },
  };

  const columns = [...productColumns, actions];

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Product Management</h1>
      <div className="flex items-center justify-between">
        <p className="text-lg">Manage all products</p>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          placeholder="Filter by Product Name"
          filterColumns={["name", "category_name"]}
        />
      )}
      <ProductEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        product={editingProduct}
        categories={categories}
      />
      <ConfirmDialog
        actionButtonRef={confirmActionButtonRef}
        open={openConfirmDialog}
        onOpenChange={setOpenConfirmDialog}
        isLoading={deleteProductMutation.isPending}
      />
    </div>
  );
}

export default Products;
